import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import type { MemoryNoticeDTO } from '@contracts/assistant-memory';
import type { Id } from '@contracts/common';
import { useNarrowView } from '../../../lib/media';
import { useAssistant } from '../AssistantProvider';
import { tabHref } from '../me/tabs';
import { tellRefusal } from '../proactive/NudgeTray';
import { discardMemoryNotice, isGone, MEMORIES_KEY, proposeMemory, type MemoryNotices } from './api';
import { MemoryText } from './MemoryText';
import { focusAfterRemoval, memoryPlainOf, rowActionName } from './rows';

/** The "Trí nhớ" tab of "Trợ lý của tôi" (S8 §9, D85). */
export const MEMORY_PAGE = tabHref('tri-nho');

/** The mockup's `.remember a`: bold primary-hover words; a 44 px target below lg, its word centred in it. */
const ACTION = 'inline-flex min-h-11 items-center justify-center font-semibold text-[hsl(var(--h-primary-hover))] hover:underline '
  + 'disabled:opacity-60 lg:min-h-0';

/** A note's "Bỏ", which the next "Bỏ" nearby moves the focus to (U-F1). */
const DROP = '[data-remember-drop]';

/** D220: when the last note leaves, the focus goes to the message log around it (focusable, out of the tab order). */
const toLog = (row: HTMLElement | null): void => { row?.closest<HTMLElement>('[role="log"]')?.focus(); };

function Note({ notice, last, onDrop, outOfLine }: {
  notice: MemoryNoticeDTO; last: boolean; onDrop: (id: Id) => void; outOfLine: (row: HTMLElement | null) => void;
}) {
  const queryClient = useQueryClient();
  const narrow = useNarrowView();
  const { setOpen } = useAssistant();
  const [busy, setBusy] = useState(false);
  const [proposed, setProposed] = useState(false);
  const [canPropose, setCanPropose] = useState(notice.canPropose);
  const row = useRef<HTMLDivElement>(null);
  const settle = () => queryClient.invalidateQueries({ queryKey: MEMORIES_KEY });

  /** U-F1: the row goes, so the focus moves first to the next note's "Bỏ", the one before, else the message log
   *  (D220: never the composer, whose focus would open a phone's keyboard over the sheet). */
  const leave = () => {
    const next = focusAfterRemoval(row.current, DROP, null, () => null);
    if (next === null) outOfLine(row.current);
    else next.focus();
    onDrop(notice.id);
  };
  const discard = async () => {
    setBusy(true);
    try {
      await discardMemoryNotice(notice);
      leave();
      void settle();
    } catch (e) {
      tellRefusal(e);
      if (isGone(e)) leave();
      else setBusy(false);
    }
  };
  const propose = async () => {
    setBusy(true);
    try {
      await proposeMemory(notice.memoryId, notice.memoryVersion);
      setProposed(true);
      void settle();
    } catch (e) {
      tellRefusal(e);
      if (isGone(e)) setCanPropose(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div ref={row} data-remember-note="" className="flex flex-wrap items-center gap-x-2">
      <span className="min-w-0 flex-1 basis-40 break-words">
        <span aria-hidden="true">✦ </span>Em đã nhớ: <b className="font-bold"><MemoryText parts={notice.parts} /></b>
      </span>
      <button type="button" data-remember-drop="" className={ACTION} disabled={busy}
        aria-label={rowActionName('Bỏ', memoryPlainOf(notice.parts))} onClick={() => { void discard(); }}>
        Bỏ
      </button>
      {proposed ? <span className="inline-flex min-h-11 items-center lg:min-h-0">Đã gửi gợi ý</span>
        : canPropose && (
          <button type="button" className={ACTION} disabled={busy} onClick={() => { void propose(); }}>
            Gợi ý cho công ty
          </button>
        )}
      {/* D193: one "Xem tất cả" for the block, on its last row; on a phone the sheet steps aside for the page. */}
      {last && (
        <Link to={MEMORY_PAGE} className={ACTION} onClick={() => { if (narrow) setOpen(false); }}>Xem tất cả</Link>
      )}
    </div>
  );
}

/**
 * "Em đã nhớ" (S3 §9.7, mockup `.remember`; D192): one block after the last message, a row per claimed note with its
 * own "Bỏ" and, when the note may go to the company, "Gợi ý cho công ty". The notes are acknowledged from the effect,
 * after the block rendered — never when the claim answered.
 */
export function RememberLine({ memory }: { memory: MemoryNotices }) {
  const { notices, painted, drop } = memory;
  useEffect(() => { painted(notices); }, [notices, painted]);
  if (notices.length === 0) return null;
  return (
    <div data-remember="" className="flex flex-col gap-1 self-stretch rounded-lg bg-[hsl(var(--h-muted))] px-2 py-1.5 text-xs text-[hsl(var(--h-muted-foreground))]">
      {notices.map((n, i) => (
        <Note key={n.id} notice={n} last={i === notices.length - 1} onDrop={drop} outOfLine={toLog} />
      ))}
    </div>
  );
}
